// Una solapa de la nomina: la tabla, el formulario de alta o edicion y la baja
// con confirmacion. Es la misma mecanica para vehiculos y conductores; lo que
// cambia (columnas y formulario) entra por props.
//
// La baja no borra: la API devuelve la fila con inRoster false y la tabla la
// muestra como "De baja". Esa fila no se edita ni se da de baja; su unica
// accion es "Reincorporar", con confirmacion (la UI ofrece, la API decide).
//
// Rediseño (docs/PLAN-REDISENO-PORTAL.md §6.6): tabla densa de 30 px que en
// 390 px se vuelve fichas; la ficha de documentos y el alta o edicion se
// abren en la hoja lateral; el resultado de cada accion va en un toast.
import { useState } from "react";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { EmptyState } from "../components/ui/empty.js";
import { Sheet } from "../components/ui/sheet.js";
import { useToast } from "../components/ui/toast.js";
import { sortRoster, upsertRow } from "../logic/fleet.js";
import { ListNotice, ReinstateConfirm, RemovalConfirm, errorCopy } from "./parts.js";
import type { RosterHandle } from "./useRoster.js";

export interface Column<T> {
  header: string;
  cell: (row: T) => React.ReactNode;
}

type Mode<T> = { kind: "list" } | { kind: "create" } | { kind: "edit"; row: T };

export interface FormSlotProps<T> {
  current?: T;
  onSaved: (row: T) => void;
  onCancel: () => void;
}

interface Props<T extends { id: string; code: string; inRoster: boolean }> {
  /** "vehículo" o "conductor". */
  noun: string;
  /** "los vehículos" o "los conductores". */
  plural: string;
  emptyCopy: string;
  roster: RosterHandle<T>;
  columns: readonly Column<T>[];
  renderForm: (props: FormSlotProps<T>) => React.ReactNode;
  remove: (row: T) => Promise<T>;
  reinstate: (row: T) => Promise<T>;
  /**
   * Ficha de documentos de una fila (PP-23). Recibe la fila vigente del
   * listado: si una relectura cambia su elegibilidad, la ficha lo ve.
   */
  renderDocuments?: (row: T, onClose: () => void) => React.ReactNode;
  /** Como se nombra la fila en el subtitulo de la hoja ("M-01 · AA100AA"). */
  rowLabel?: (row: T) => string;
}

/** Que fila esta pidiendo confirmacion, y de que. */
type Confirming = { id: string; action: "remove" | "reinstate" } | null;

export function RosterPanel<T extends { id: string; code: string; inRoster: boolean }>({
  noun,
  plural,
  emptyCopy,
  roster,
  columns,
  renderForm,
  remove,
  reinstate,
  renderDocuments,
  rowLabel,
}: Props<T>): React.JSX.Element {
  const { notify } = useToast();
  const [mode, setMode] = useState<Mode<T>>({ kind: "list" });
  const [documentsOf, setDocumentsOf] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const { state } = roster;

  function saved(row: T, verb: string): void {
    roster.update((items) => upsertRow(items, row));
    setMode({ kind: "list" });
    notify(`${capitalize(noun)} ${row.code} ${verb}.`, "ok");
  }

  /** Baja o reincorporacion confirmada: la fila se reemplaza con lo que devolvio la API. */
  function confirmAction(row: T, action: "remove" | "reinstate"): void {
    setRemoving(row.id);
    (action === "remove" ? remove(row) : reinstate(row))
      .then((updated) => {
        roster.update((items) => upsertRow(items, updated));
        const verb = action === "remove" ? "dado de baja de la nómina" : "reincorporado a la nómina";
        notify(`${capitalize(noun)} ${updated.code} ${verb}.`, "info");
      })
      .catch((error: unknown) => {
        notify(errorCopy(error, action), "error");
      })
      .finally(() => {
        setRemoving(null);
        setConfirming(null);
      });
  }

  const documentsRow = renderDocuments !== undefined && documentsOf !== null && state.estado === "listo" ? state.items.find((item) => item.id === documentsOf) : undefined;
  const label = (row: T) => (rowLabel === undefined ? row.code : rowLabel(row));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={state.estado !== "listo" || mode.kind !== "list"}
          onClick={() => setMode({ kind: "create" })}
        >
          Alta de {noun}
        </Button>
        <Button size="sm" variant="secundario" disabled={state.estado === "cargando"} onClick={roster.reload}>
          Actualizar
        </Button>
      </div>

      <Sheet
        open={mode.kind !== "list"}
        onClose={() => setMode({ kind: "list" })}
        title={mode.kind === "edit" ? `Editar ${noun} ${mode.row.code}` : `Alta de ${noun}`}
        {...(mode.kind === "edit" ? { subtitle: label(mode.row) } : {})}
      >
        {mode.kind === "create" && renderForm({ onSaved: (row) => saved(row, "dado de alta"), onCancel: () => setMode({ kind: "list" }) })}
        {mode.kind === "edit" &&
          renderForm({
            current: mode.row,
            onSaved: (row) => saved(row, "actualizado"),
            onCancel: () => setMode({ kind: "list" }),
          })}
      </Sheet>

      <Sheet open={documentsRow !== undefined} onClose={() => setDocumentsOf(null)} title="Documentos" {...(documentsRow === undefined ? {} : { subtitle: label(documentsRow) })}>
        {documentsRow !== undefined && renderDocuments !== undefined && renderDocuments(documentsRow, () => setDocumentsOf(null))}
      </Sheet>

      <ListNotice state={state} what={plural} onRetry={roster.reload} />

      {state.estado === "listo" && state.items.length === 0 && <EmptyState icon="nomina" title={emptyCopy} />}

      {state.estado === "listo" && state.items.length > 0 && (
        <Card className="p-0">
          <div className="overflow-x-auto">
            <table className="tabla-fichas w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-divisor">
                  {columns.map((column) => (
                    <th key={column.header} scope="col" className="rotulo h-10 whitespace-nowrap px-3.5 text-texto-suave">
                      {column.header}
                    </th>
                  ))}
                  <th scope="col" className="rotulo h-10 px-3.5 text-texto-suave">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortRoster(state.items).map((row) => (
                  <tr key={row.id} className={`border-b border-divisor align-middle last:border-b-0 hover:bg-velo ${row.inRoster ? "" : "text-texto-suave"}`}>
                    {columns.map((column) => (
                      <td key={column.header} data-rotulo={column.header} className="min-h-fila px-3.5 py-1.5 text-texto">
                        {column.cell(row)}
                      </td>
                    ))}
                    <td data-rotulo="Acciones" className="px-3.5 py-1.5">
                      {confirming?.id === row.id && confirming.action === "remove" ? (
                        <RemovalConfirm
                          subject={row.code}
                          busy={removing === row.id}
                          onConfirm={() => confirmAction(row, "remove")}
                          onCancel={() => setConfirming(null)}
                        />
                      ) : confirming?.id === row.id && confirming.action === "reinstate" ? (
                        <ReinstateConfirm
                          subject={row.code}
                          busy={removing === row.id}
                          onConfirm={() => confirmAction(row, "reinstate")}
                          onCancel={() => setConfirming(null)}
                        />
                      ) : !row.inRoster ? (
                        <Button
                          size="sm"
                          variant="secundario"
                          aria-label={`Reincorporar ${row.code}`}
                          disabled={mode.kind !== "list" || removing !== null}
                          onClick={() => setConfirming({ id: row.id, action: "reinstate" })}
                        >
                          Reincorporar
                        </Button>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {renderDocuments !== undefined && (
                            <Button
                              size="sm"
                              variant="secundario"
                              aria-label={`Documentos de ${row.code}`}
                              aria-pressed={documentsOf === row.id}
                              onClick={() => setDocumentsOf(documentsOf === row.id ? null : row.id)}
                            >
                              Documentos
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="secundario"
                            aria-label={`Editar ${row.code}`}
                            disabled={!row.inRoster || mode.kind !== "list"}
                            onClick={() => setMode({ kind: "edit", row })}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="fantasma"
                            aria-label={`Dar de baja ${row.code}`}
                            disabled={!row.inRoster || mode.kind !== "list" || removing !== null}
                            onClick={() => setConfirming({ id: row.id, action: "remove" })}
                          >
                            Dar de baja
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
